import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Checkbox,
  Space,
  Button,
  Divider,
  Alert,
  Steps,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { SuppliersApi, ProjectsApi, UnitCategoriesApi } from 'api/api';
import { PaymentMethod, SupplierDto, ProjectDto, UnitCategoryDto, SupplierPaymentDto } from 'api/models';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import openNotificationWithIcon from 'utility/notification';
import FeatherIcon from 'feather-icons-react';
import { useMediaQuery } from 'react-responsive';
import { formatCurrency } from 'utility/utility';

const { Step } = Steps;
const suppliersApi = new SuppliersApi();
const projectsApi = new ProjectsApi();
const categoriesApi = new UnitCategoriesApi();

interface SupplierPaymentFormValues {
  supplierId: number;
  amount: number;
  description: string;
  dueDate: dayjs.Dayjs;
  paidDate?: dayjs.Dayjs;
  paymentMethod?: PaymentMethod;
  transactionReference?: string;
  notes?: string;
  projectId?: number;
  categoryId?: number;
  newPaidAmount?: number;
}

const CreateSupplierPaymentPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get('supplierId');
  const isEditMode = !!id;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // Data states
  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [categories, setCategories] = useState<UnitCategoryDto[]>([]);

  // UI states
  const [showProject, setShowProject] = useState(false);
  const [showCategory, setShowCategory] = useState(false);
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);

  // Set initial supplier if provided in query params
  useEffect(() => {
    if (!isEditMode && supplierId) {
      form.setFieldValue('supplierId', Number(supplierId));
    }
  }, [supplierId, isEditMode, form]);

  // Fetch payment details if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchPaymentDetails = async () => {
        try {
          const response = await suppliersApi.apiSuppliersPaymentsIdGet({ id: Number(id) });
          const payment = response.data;

          if (payment.isPaid) {
            setIsAlreadyPaid(true);
            form.setFieldsValue(payment);
          }

          setShowProject(!!payment.projectId);
          setShowCategory(!!payment.categoryId);

          form.setFieldsValue({
            ...payment,
            dueDate: dayjs(payment.dueDate),
            paidDate: payment.paidDate ? dayjs(payment.paidDate) : undefined,
            newPaidAmount: payment.paidAmount,
          });
        } catch (error) {
          openNotificationWithIcon('error', t('suppliers:payment.load.error', 'Failed to load payment details'));
          navigate('/supplier-payments');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchPaymentDetails();
    }
  }, [id, form, isEditMode]);

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await suppliersApi.apiSuppliersGet({ pageSize: -1 });
        setSuppliers(response.data.items || []);

        // If we have a supplierId but supplier isn't loaded yet, fetch it specifically
        if (supplierId && !response.data.items?.some(s => s.id === Number(supplierId))) {
          try {
            const supplierResponse = await suppliersApi.apiSuppliersIdGet({ id: Number(supplierId) });
            setSuppliers(prev => [...prev, supplierResponse.data]);
          } catch (error) {
            console.error('Failed to fetch specific supplier:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      }
    };
    fetchSuppliers();
  }, [supplierId]);

  // Fetch projects
  useEffect(() => {
    if (showProject) {
      const fetchProjects = async () => {
        try {
          const response = await projectsApi.apiProjectsGet({ pageSize: -1 });
          setProjects(response.data.items || []);
        } catch (error) {
          console.error('Failed to fetch projects:', error);
        }
      };
      fetchProjects();
    }
  }, [showProject]);

  // Fetch categories based on selected project
  useEffect(() => {
    if (showCategory && form.getFieldValue('projectId')) {
      const fetchCategories = async () => {
        try {
          const response = await categoriesApi.apiUnitCategoriesGet({
            projectId: form.getFieldValue('projectId'),
            pageSize: -1,
          });
          setCategories(response.data.items || []);
        } catch (error) {
          console.error('Failed to fetch categories:', error);
        }
      };
      fetchCategories();
    }
  }, [showCategory, form.getFieldValue('projectId')]);

  const handleSubmit = async (values: any) => {
    console.log('Form values received:', values); // Debug log
    try {
      setLoading(true);

      // Log the values to verify what's coming from the form
      console.log('Form values:', values);

      // Make sure to create a new object for the API call to avoid reference issues
      const paymentData = {
        id: Number(id),
        supplierId: values.supplierId,
        amount: values.amount,
        newPaidAmount: values.newPaidAmount,
        description: values.description,
        dueDate: values.dueDate.toISOString(),
        projectId: showProject && values.projectId ? values.projectId : undefined,
        categoryId: showProject && showCategory && values.categoryId ? values.categoryId : undefined,
      };

      // Log the final data being sent to the API
      console.log('API payload:', paymentData);

      if (isEditMode) {
        await suppliersApi.apiSuppliersPaymentsIdPut({
          id: Number(id),
          updateSupplierPaymentCommand: paymentData,
        });
        openNotificationWithIcon('success', t('suppliers:payment.updated', 'Payment updated successfully'));
      } else {
        await suppliersApi.apiSuppliersPaymentsPost({
          createSupplierPaymentCommand: paymentData,
        });
        openNotificationWithIcon('success', t('suppliers:payment.created', 'Payment created successfully'));
      }
      navigate('/supplier-payments');
    } catch (error) {
      console.error('Submission error:', error);
      openNotificationWithIcon(
        'error',
        isEditMode
          ? t('suppliers:payment.update.error', 'Failed to update payment')
          : t('suppliers:payment.create.error', 'Failed to create payment'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/supplier-payments');
  };

  if (initialLoading) {
    return (
      <Main>
        <Card loading={true} />
      </Main>
    );
  }

  return (
    <>
      <PageHeader
        ghost
        title={
          isEditMode
            ? isAlreadyPaid
              ? t('suppliers:view-payment')
              : t('suppliers:edit-payment')
            : t('suppliers:create-payment')
        }
      />
      <Main>
        {isAlreadyPaid && (
          <Alert
            message={t('suppliers:payment-view-only', 'This payment is already paid. You can only view the details.')}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}
        {form.getFieldValue('paidAmount') > 0 && !isAlreadyPaid && (
          <Alert
            message={t('suppliers:current-paid-amount', 'Current Paid Amount: {{amount}}', {
              amount: formatCurrency(form.getFieldValue('paidAmount')),
            })}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Card>
          <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isAlreadyPaid}>
            {/* Remove the conditional rendering and put all Form.Items directly here */}
            <Form.Item name="supplierId" label={t('suppliers:supplier')} rules={[{ required: true }]}>
              <Select>
                {suppliers.map(supplier => (
                  <Select.Option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label={t('suppliers:amount')}
              rules={[{ required: true, message: t('suppliers:amount-required', 'Please enter the payment amount') }]}
            >
              <InputNumber
                className="w-full"
                style={{ width: '100%' }}
                formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/€\s?|(,*)/g, '')}
              />
            </Form.Item>

            {isEditMode && (
              <Form.Item
                name="newPaidAmount"
                label={t('suppliers:paid-amount', 'Paid Amount')}
                rules={[
                  { required: false },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const totalAmount = getFieldValue('amount');
                      const currentPaidAmount = form.getFieldValue('paidAmount') || 0;

                      if (!value) return Promise.resolve();
                      if (value < currentPaidAmount) {
                        return Promise.reject(
                          t(
                            'suppliers:paid-amount-less-error',
                            'New paid amount cannot be less than current paid amount',
                          ),
                        );
                      }
                      if (value > totalAmount) {
                        return Promise.reject(
                          t('suppliers:paid-amount-exceed-error', 'Paid amount cannot exceed total amount'),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  className="w-full"
                  style={{ width: '100%' }}
                  formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/€\s?|(,*)/g, '')}
                  min={form.getFieldValue('paidAmount') || 0}
                  max={form.getFieldValue('amount')}
                />
              </Form.Item>
            )}

            <Form.Item name="description" label={t('suppliers:description')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item name="dueDate" label={t('suppliers:due-date')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox checked={showProject} onChange={e => setShowProject(e.target.checked)}>
                {t('suppliers:link-to-project')}
              </Checkbox>

              {showProject && (
                <Form.Item name="projectId" label={t('suppliers:project')}>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder={t('suppliers:select-project')}
                    onChange={() => form.setFieldValue('categoryId', undefined)}
                  >
                    {projects.map(project => (
                      <Select.Option key={project.id} value={project.id}>
                        {project.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Checkbox
                checked={showCategory}
                onChange={e => setShowCategory(e.target.checked)}
                disabled={!showProject}
              >
                {t('suppliers:link-to-category')}
              </Checkbox>

              {showCategory && showProject && (
                <Form.Item name="categoryId" label={t('suppliers:category')}>
                  <Select showSearch optionFilterProp="children" placeholder={t('suppliers:select-category')}>
                    {categories.map(category => (
                      <Select.Option key={category.id} value={category.id}>
                        {category.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Space>

            <Button type="primary" htmlType="submit" loading={loading} style={{ marginTop: '16px' }}>
              {isEditMode ? t('global:update', 'Update') : t('global:create', 'Create')}
            </Button>
          </Form>
        </Card>
      </Main>
    </>
  );
};

export default CreateSupplierPaymentPage;
